/**
 * "Feature flags" section for the product item drawer.
 *
 * Lists feature flags linked to this product (feature_flags.product_card_id)
 * with lifecycle stage and active org-rollout count, and lets superadmins
 * attach existing flags / detach linked ones. Linked flags drive the card's
 * lifecycle stage (server-side sync), so attaching a flag immediately pulls
 * the card to its flag-derived stage. Superadmin only — flags are a
 * superadmin domain and the lookups hit superadmin endpoints.
 */
import React from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  Flex,
  TagBadge,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { Flag, Plus, X } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useFeatureFlagsQuery,
  useProductCardFlagsQuery,
  useSetFlagProductCardMutation,
} from '../../../ui/pages/superadmin/useSuperadminData';
import { SectionLabel } from '../../cards/components/SectionLabel';

interface Props {
  productItemId: string;
}

export function ProductItemFlagsSection({ productItemId }: Props) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const { toast } = useToast();

  const flagsQuery = useProductCardFlagsQuery(isSuperadmin ? productItemId : null);
  const setProductCard = useSetFlagProductCardMutation();

  const [pickerOpen, setPickerOpen] = React.useState(false);

  if (!isSuperadmin) return null;

  const flags = flagsQuery.data?.flags ?? [];

  const detach = (flagName: string) => {
    setProductCard.mutate(
      { flagName, productCardId: null },
      {
        onSuccess: () => toast.success(`Unlinked ${flagName}`),
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <section className="space-y-3 border-t border-border pt-6">
      <Flex align="center" justify="between">
        <SectionLabel>Feature flags</SectionLabel>
        <DialogTrigger isOpen={pickerOpen} onOpenChange={setPickerOpen}>
          <Button variant="ghost" size="sm" iconLeft={<Plus size={13} weight="bold" />}>
            Link flag
          </Button>
          <Dialog
            title="Link a feature flag"
            description="The flag's rollout state will drive this card's stage from now on (the card moves to its flag-derived column immediately)."
          >
            <FlagPicker
              productItemId={productItemId}
              linkedFlagIds={flags.map((f) => f.id)}
              onDone={() => setPickerOpen(false)}
            />
          </Dialog>
        </DialogTrigger>
      </Flex>
      {flags.length === 0 ? (
        <Text size="xs" className="text-muted-foreground">
          No linked feature flags.
        </Text>
      ) : (
        flags.map((flag) => (
          <Flex key={flag.id} gap="xs" align="center" wrap>
            <Flag size={12} className="shrink-0 text-muted-foreground" />
            <Text size="xs" className="font-mono">
              {flag.name}
            </Text>
            {flag.lifecycleStage !== 'active' && (
              <TagBadge
                name={flag.lifecycleStage === 'baking' ? '🔥 baking' : flag.lifecycleStage}
                color={
                  flag.lifecycleStage === 'baking'
                    ? 'blue'
                    : flag.lifecycleStage === 'internal'
                      ? 'purple'
                      : 'gray'
                }
                hideDot
                size="sm"
              />
            )}
            <Text size="xs" className="text-muted-foreground">
              {flag.activeOrgGateCount} org{flag.activeOrgGateCount === 1 ? '' : 's'}
            </Text>
            {flag.rolloutPercentage !== null && (
              <TagBadge
                name={`A/B ${flag.rolloutPercentage}%`}
                color="orange"
                hideDot
                size="sm"
              />
            )}
            <button
              type="button"
              onClick={() => detach(flag.name)}
              disabled={setProductCard.isPending}
              title={`Unlink ${flag.name}`}
              aria-label={`Unlink ${flag.name}`}
              className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <X size={12} weight="bold" />
            </button>
          </Flex>
        ))
      )}
    </section>
  );
}

/** Searchable list of all flags; selecting one links (or relinks) it to this card. */
function FlagPicker({
  productItemId,
  linkedFlagIds,
  onDone,
}: {
  productItemId: string;
  linkedFlagIds: string[];
  onDone: () => void;
}) {
  const { toast } = useToast();
  const allFlagsQuery = useFeatureFlagsQuery();
  const setProductCard = useSetFlagProductCardMutation();
  const [search, setSearch] = React.useState('');

  const candidates = React.useMemo(() => {
    const all = allFlagsQuery.data?.flags ?? [];
    const term = search.trim().toLowerCase();
    return all
      .filter((f) => !linkedFlagIds.includes(f.id))
      .filter((f) => (term ? f.name.toLowerCase().includes(term) : true))
      .slice(0, 25);
  }, [allFlagsQuery.data, linkedFlagIds, search]);

  const link = (flagName: string) => {
    setProductCard.mutate(
      { flagName, productCardId: productItemId },
      {
        onSuccess: () => {
          toast.success(`Linked ${flagName}`);
          onDone();
        },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <div className="space-y-3">
      <TextField
        aria-label="Search feature flags"
        placeholder="Search flags…"
        value={search}
        onChange={setSearch}
        autoFocus
      />
      <div className="max-h-72 space-y-0.5 overflow-y-auto">
        {allFlagsQuery.isLoading ? (
          <Text size="xs" className="text-muted-foreground">
            Loading flags…
          </Text>
        ) : candidates.length === 0 ? (
          <Text size="xs" className="text-muted-foreground">
            No matching flags.
          </Text>
        ) : (
          candidates.map((flag) => (
            <button
              key={flag.id}
              type="button"
              onClick={() => link(flag.name)}
              disabled={setProductCard.isPending}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted disabled:opacity-50"
            >
              <Flag size={12} className="shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{flag.name}</span>
              {flag.productTitle && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  linked to {flag.productTitle}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
